import React from 'react';
import { Card, Space, Switch, Divider, Form, InputNumber, Tag, Dropdown, Button, Typography, Tooltip, Input } from 'antd';
import { SettingOutlined, PlusOutlined, DeleteOutlined, DownOutlined, CloseOutlined, SaveOutlined } from '@ant-design/icons';

const { Text } = Typography;

interface DeviceSettings {
  id: string;
  profile_name: string;
  photo_enabled: boolean;
  audio_enabled: boolean;
  gps_enabled: boolean;
  photo_interval: number;
  gps_interval: number;
  is_active: boolean;
  created_at?: string;
  updated_at?: string;
}

interface DeviceSettingsProps {
  deviceSettings: {
    photo_enabled: boolean;
    audio_enabled: boolean;
    gps_enabled: boolean;
    photo_interval: number;
    gps_interval: number;
  };
  isEditing: boolean;
  settingsForm: import('antd').FormInstance;
  settingsProfiles: DeviceSettings[];
  activeProfile: string;
  isOnline?: boolean;
  onSwitchProfile: (profileName: string) => void;
  onDeleteProfile: (profileName: string) => void;
  onCreateProfile: () => void;
  onEditModeChange?: (editing: boolean) => void;
  onSave?: () => void;
  cameraConfig: {
    revision: number;
    overrides: Record<string, string>;
    updated_at?: string | null;
    delivered?: boolean | null;
  };
  cameraSetDraft: string;
  cameraClearDraft: string;
  onCameraSetDraftChange: (value: string) => void;
  onCameraClearDraftChange: (value: string) => void;
  onApplyCameraConfigPatch: () => void;
  cameraPatchLoading?: boolean;
}

const DeviceSettingsComponent: React.FC<DeviceSettingsProps> = ({
  deviceSettings,
  isEditing,
  settingsForm,
  settingsProfiles,
  activeProfile,
  isOnline = false,
  onSwitchProfile,
  onDeleteProfile,
  onCreateProfile,
  onEditModeChange,
  onSave,
  cameraConfig,
  cameraSetDraft,
  cameraClearDraft,
  onCameraSetDraftChange,
  onCameraClearDraftChange,
  onApplyCameraConfigPatch,
  cameraPatchLoading = false,
}) => {
  const overridesText = Object.keys(cameraConfig.overrides).length > 0
    ? JSON.stringify(cameraConfig.overrides, null, 2)
    : '{}';

  return (
    <Card 
      title={<span style={{ fontSize: '12px' }}>デバイス設定</span>}
      extra={
        !isEditing ? (
          <div style={{ display: 'flex', gap: 8 }}>
            <Tag color="default" style={{ fontSize: '12px', margin: 0, minWidth: 60, textAlign: 'center', color: 'rgba(0, 0, 0, 0.25)' }}>{activeProfile}</Tag>
            <Tooltip 
              title={!isOnline ? "WebSocket接続中のみ編集できます" : ""}
              overlayStyle={{ fontSize: '12px' }}
              color="#666"
            >
              <Button 
                type="primary" 
                size="small" 
                icon={<SettingOutlined />}
                disabled={!isOnline}
                style={{ fontSize: '12px', minWidth: 60 }}
                onClick={() => onEditModeChange?.(true)}
              >
                設定変更
              </Button>
            </Tooltip>
          </div>
        ) : (
          <Space>
            <Dropdown
              menu={{
                items: settingsProfiles.map(profile => ({
                  key: profile.profile_name,
                  label: (
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', minWidth: 150 }}>
                      <span>{profile.profile_name} {profile.is_active && <Tag color="blue">アクティブ</Tag>}</span>
                      {profile.profile_name !== 'default' && (
                        <Button 
                          type="text" 
                          size="small" 
                          icon={<DeleteOutlined />} 
                          onClick={(e) => {
                            e.stopPropagation();
                            onDeleteProfile(profile.profile_name);
                          }}
                          danger
                        />
                      )}
                    </div>
                  ),
                  onClick: () => !profile.is_active && onSwitchProfile(profile.profile_name)
                })),
                onClick: (e) => e.domEvent.stopPropagation()
              }}
            >
              <Button size="small">
                {activeProfile} <DownOutlined />
              </Button>
            </Dropdown>
            <Button 
              type="primary" 
              size="small" 
              icon={<PlusOutlined />}
              onClick={onCreateProfile}
            >
              新規作成
            </Button>
            <Button 
              size="small"
              icon={<CloseOutlined />}
              onClick={() => onEditModeChange?.(false)}
            >
              キャンセル
            </Button>
            <Button 
              type="primary"
              size="small"
              icon={<SaveOutlined />}
              onClick={() => onSave?.()}
            >
              保存
            </Button>
          </Space>
        )
      }
      size="small"
      style={{ fontSize: '12px' }}
    >

      {!isEditing ? (
        <div>
          <div>
            <h4>データ送信設定</h4>
            <Space direction="vertical" size="middle" style={{ width: '100%' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span>写真データ送信</span>
                <Switch checked={deviceSettings.photo_enabled} disabled />
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span>音声データ送信</span>
                <Switch checked={deviceSettings.audio_enabled} disabled />
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span>GPSデータ送信</span>
                <Switch checked={deviceSettings.gps_enabled} disabled />
              </div>
            </Space>
          </div>
          <Divider />
          <div>
            <h4>送信間隔設定</h4>
            <Space direction="vertical" size="middle" style={{ width: '100%' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span>写真撮影間隔</span>
                <span>{deviceSettings.photo_interval}秒</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span>GPS送信間隔</span>
                <span>{deviceSettings.gps_interval}秒</span>
              </div>
            </Space>
          </div>
          <Divider />
          <div>
            <h4>カメラ設定オーバーライド</h4>
            <Space direction="vertical" size="small" style={{ width: '100%' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span>Revision</span>
                <span>{cameraConfig.revision}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
                <span>更新時刻</span>
                <Text style={{ fontSize: '12px' }} ellipsis>
                  {cameraConfig.updated_at || '---'}
                </Text>
              </div>
              <Input.TextArea
                value={overridesText}
                autoSize={{ minRows: 4, maxRows: 8 }}
                readOnly
              />
            </Space>
          </div>
        </div>
      ) : (
        <Form form={settingsForm} layout="vertical">
          <div>
            <h4>データ送信設定</h4>
            <Space direction="vertical" size="middle" style={{ width: '100%' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span>写真データ送信</span>
                <Form.Item name="photo_enabled" valuePropName="checked" style={{ margin: 0 }}>
                  <Switch />
                </Form.Item>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span>音声データ送信</span>
                <Form.Item name="audio_enabled" valuePropName="checked" style={{ margin: 0 }}>
                  <Switch />
                </Form.Item>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span>GPSデータ送信</span>
                <Form.Item name="gps_enabled" valuePropName="checked" style={{ margin: 0 }}>
                  <Switch />
                </Form.Item>
              </div>
            </Space>
          </div>
          <Divider />
          <div>
            <h4>送信間隔設定</h4>
            <Space direction="vertical" size="middle" style={{ width: '100%' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span>写真撮影間隔（秒）</span>
                <Form.Item name="photo_interval" style={{ margin: 0 }}>
                  <InputNumber min={1} max={300} />
                </Form.Item>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span>GPS送信間隔（秒）</span>
                <Form.Item name="gps_interval" style={{ margin: 0 }}>
                  <InputNumber min={1} max={60} />
                </Form.Item>
              </div>
            </Space>
          </div>
          <Divider />
          <div>
            <h4>カメラ設定オーバーライド</h4>
            <Space direction="vertical" size="small" style={{ width: '100%' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span>Revision</span>
                <span>{cameraConfig.revision}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
                <span>更新時刻</span>
                <Text style={{ fontSize: '12px' }} ellipsis>
                  {cameraConfig.updated_at || '---'}
                </Text>
              </div>
              <Text style={{ fontSize: '12px' }}>
                `set` JSON
              </Text>
              <Input.TextArea
                value={cameraSetDraft}
                autoSize={{ minRows: 4, maxRows: 8 }}
                onChange={(e) => onCameraSetDraftChange(e.target.value)}
                placeholder={'{"camera.capture.interval_sec":"10"}'}
              />
              <Text style={{ fontSize: '12px' }}>
                `clear` keys（改行またはカンマ区切り）
              </Text>
              <Input.TextArea
                value={cameraClearDraft}
                autoSize={{ minRows: 2, maxRows: 6 }}
                onChange={(e) => onCameraClearDraftChange(e.target.value)}
                placeholder={'camera.capture.interval_sec'}
              />
              <Button
                type="primary"
                onClick={onApplyCameraConfigPatch}
                loading={cameraPatchLoading}
              >
                カメラ設定を適用
              </Button>
              <Input.TextArea
                value={overridesText}
                autoSize={{ minRows: 4, maxRows: 8 }}
                readOnly
              />
            </Space>
          </div>
        </Form>
      )}
    </Card>
  );
};

export default DeviceSettingsComponent;
